import React, { useState, useRef } from 'react';

const ALLOWED_EXTENSIONS = ['pdf', 'png', 'jpg', 'jpeg', 'gif', 'webp'];
const MAX_FILE_SIZE_MB = 10;
const MAX_TOTAL_SIZE_MB = 20;
const MAX_FILES_COUNT = 5;

export default function ContactForm({ className = '' }) {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    message: '',
    website_url: '' // Honeypot antispam felt
  });

  const [files, setFiles] = useState([]);
  const [fileError, setFileError] = useState('');
  const [status, setStatus] = useState('idle'); // 'idle' | 'submitting' | 'success' | 'error'
  const [errorMessage, setErrorMessage] = useState('');
  const [isDragging, setIsDragging] = useState(false);

  const fileInputRef = useRef(null);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value
    }));
  };

  // Validering og tilføjelse af filer
  const handleFiles = (newFileList) => {
    setFileError('');
    const incomingFiles = Array.from(newFileList);

    if (files.length + incomingFiles.length > MAX_FILES_COUNT) {
      setFileError(`Du kan maksimalt vedhæfte ${MAX_FILES_COUNT} filer i alt.`);
      return;
    }

    const validNewFiles = [];
    let currentTotalSize = files.reduce((acc, f) => acc + f.size, 0);

    for (const file of incomingFiles) {
      const ext = file.name.split('.').pop().toLowerCase();
      if (!ALLOWED_EXTENSIONS.includes(ext)) {
        setFileError(`Filtypen .${ext} er ikke tilladt. Tilladte formater: PDF, PNG, JPG, JPEG, GIF, WEBP.`);
        return;
      }

      if (file.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
        setFileError(`Filen "${file.name}" overskrider grænsen på ${MAX_FILE_SIZE_MB} MB.`);
        return;
      }

      if (currentTotalSize + file.size > MAX_TOTAL_SIZE_MB * 1024 * 1024) {
        setFileError(`Den samlede filstørrelse må højst være ${MAX_TOTAL_SIZE_MB} MB.`);
        return;
      }

      currentTotalSize += file.size;
      validNewFiles.push(file);
    }

    setFiles((prev) => [...prev, ...validNewFiles]);
  };

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      handleFiles(e.target.files);
    }
  };

  const removeFile = (indexToRemove) => {
    setFiles((prev) => prev.filter((_, idx) => idx !== indexToRemove));
    setFileError('');
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      handleFiles(e.dataTransfer.files);
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    else if (bytes < 1048576) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / 1048576).toFixed(1) + ' MB';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus('submitting');
    setErrorMessage('');

    // Honeypot tjek (hvis udfyldt, simuler succes for at narre bot)
    if (formData.website_url) {
      setTimeout(() => {
        setStatus('success');
        setFormData({ name: '', phone: '', email: '', message: '', website_url: '' });
        setFiles([]);
      }, 800);
      return;
    }

    try {
      // Byg FormData til multipart/form-data submission
      const data = new FormData();
      data.append('name', formData.name);
      data.append('phone', formData.phone);
      data.append('email', formData.email);
      data.append('message', formData.message);
      data.append('website_url', formData.website_url);

      // Tilføj filer til FormData
      files.forEach((file) => {
        data.append('attachments', file);
      });

      const response = await fetch('https://api.dev.auxo.dk/webform/submit', {
        method: 'POST',
        body: data // Bemærk: Aldrig Content-Type header her; browseren tilføjer boundary automatisk
      });

      if (!response.ok) {
        throw new Error('Der opstod en fejl ved afsendelse af formularen. Prøv venligst igen.');
      }

      const result = await response.json();
      if (result.status === 'ok') {
        setStatus('success');
        setFormData({ name: '', phone: '', email: '', message: '', website_url: '' });
        setFiles([]);
      } else {
        throw new Error(result.message || 'Der opstod en fejl under valideringen.');
      }
    } catch (error) {
      console.error('Formularfejl:', error);
      setStatus('error');
      setErrorMessage(error.message || 'Der opstod en uventet fejl. Prøv venligst igen senere.');
    }
  };

  return (
    <div className={`rounded-3xl border border-gray-200/90 bg-white p-6 sm:p-8 lg:p-10 shadow-xs ${className}`}>
      <div className="space-y-1">
        <h3 className="text-2xl font-bold text-gray-900">Send os en besked</h3>
        <p className="text-sm text-gray-500">
          Udfyld formularen nedenfor og vedhæft eventuelle billeder eller skitser. Vi vender hurtigt tilbage.
        </p>
      </div>

      {status === 'success' ? (
        <div className="mt-8 rounded-2xl bg-emerald-50 p-6 text-emerald-900 border border-emerald-200 flex items-start gap-4">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-emerald-100 text-emerald-700">
            <i className="fa-solid fa-circle-check text-xl" />
          </div>
          <div className="space-y-1">
            <h4 className="text-lg font-bold">Mange tak for din henvendelse!</h4>
            <p className="text-sm text-emerald-800 leading-relaxed">
              Vi har modtaget din besked og dine eventuelle vedhæftede filer. Vi behandler din forespørgsel og vender tilbage til dig hurtigst muligt.
            </p>
            <div className="pt-3">
              <button
                type="button"
                onClick={() => setStatus('idle')}
                className="text-xs font-bold text-emerald-800 hover:underline cursor-pointer"
              >
                Send en ny besked &rarr;
              </button>
            </div>
          </div>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="mt-8 space-y-6">
          
          {/* Honeypot felt - Absolut skjult for mennesker */}
          <div className="sr-only absolute -top-[9999px] -left-[9999px]" aria-hidden="true">
            <label htmlFor="website_url">Efterlad dette felt tomt</label>
            <input
              type="text"
              id="website_url"
              name="website_url"
              value={formData.website_url}
              onChange={handleChange}
              tabIndex="-1"
              autoComplete="off"
            />
          </div>

          {/* Navn */}
          <div>
            <label htmlFor="name" className="block text-sm font-semibold text-gray-800">
              Dit fulde navn <span className="text-brand-red">*</span>
            </label>
            <div className="mt-1.5 relative rounded-xl shadow-xs">
              <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3.5 text-gray-400">
                <i className="fa-solid fa-user text-sm" />
              </div>
              <input
                type="text"
                id="name"
                name="name"
                required
                value={formData.name}
                onChange={handleChange}
                placeholder="F.eks. Hans Hansen"
                className="block w-full rounded-xl border border-gray-300 bg-slate-50/50 py-3 pl-10 pr-4 text-sm text-gray-900 focus:border-brand-red focus:bg-white focus:ring-2 focus:ring-brand-red/20 focus:outline-none transition-all"
              />
            </div>
          </div>

          {/* Telefon & Email */}
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
            <div>
              <label htmlFor="phone" className="block text-sm font-semibold text-gray-800">
                Telefonnummer <span className="text-brand-red">*</span>
              </label>
              <div className="mt-1.5 relative rounded-xl shadow-xs">
                <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3.5 text-gray-400">
                  <i className="fa-solid fa-phone text-sm" />
                </div>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  required
                  value={formData.phone}
                  onChange={handleChange}
                  placeholder="F.eks. 70 60 47 88"
                  className="block w-full rounded-xl border border-gray-300 bg-slate-50/50 py-3 pl-10 pr-4 text-sm text-gray-900 focus:border-brand-red focus:bg-white focus:ring-2 focus:ring-brand-red/20 focus:outline-none transition-all"
                />
              </div>
            </div>

            <div>
              <label htmlFor="email" className="block text-sm font-semibold text-gray-800">
                E-mailadresse <span className="text-brand-red">*</span>
              </label>
              <div className="mt-1.5 relative rounded-xl shadow-xs">
                <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3.5 text-gray-400">
                  <i className="fa-solid fa-envelope text-sm" />
                </div>
                <input
                  type="email"
                  id="email"
                  name="email"
                  required
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="F.eks. navn@firma.dk"
                  className="block w-full rounded-xl border border-gray-300 bg-slate-50/50 py-3 pl-10 pr-4 text-sm text-gray-900 focus:border-brand-red focus:bg-white focus:ring-2 focus:ring-brand-red/20 focus:outline-none transition-all"
                />
              </div>
            </div>
          </div>

          {/* Beskrivelse */}
          <div>
            <label htmlFor="message" className="block text-sm font-semibold text-gray-800">
              Beskrivelse af opgaven <span className="text-brand-red">*</span>
            </label>
            <div className="mt-1.5 relative rounded-xl shadow-xs">
              <textarea
                id="message"
                name="message"
                required
                rows="4"
                value={formData.message}
                onChange={handleChange}
                placeholder="Beskriv grundigt hvad opgaven drejer sig om (f.eks. bilmodel, mål, farveønsker osv.)..."
                className="block w-full rounded-xl border border-gray-300 bg-slate-50/50 p-4 text-sm text-gray-900 focus:border-brand-red focus:bg-white focus:ring-2 focus:ring-brand-red/20 focus:outline-none transition-all"
              />
            </div>
          </div>

          {/* Fil-upload sektion */}
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <label className="block text-sm font-semibold text-gray-800">
                Vedhæft filer & billeder <span className="text-xs font-normal text-gray-500">(Valgfrit)</span>
              </label>
              <span className="text-xs text-gray-400">Maks 5 filer (i alt 20 MB)</span>
            </div>

            {/* Drag and Drop zone */}
            <div
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              onClick={() => fileInputRef.current?.click()}
              className={`flex flex-col items-center justify-center rounded-2xl border-2 border-dashed p-6 text-center cursor-pointer transition-all duration-200 ${
                isDragging
                  ? 'border-brand-red bg-brand-green-light/20 scale-[1.01]'
                  : 'border-gray-300 bg-slate-50/50 hover:bg-slate-100/60 hover:border-gray-400'
              }`}
            >
              <input
                ref={fileInputRef}
                type="file"
                name="attachments"
                multiple
                accept=".pdf,.png,.jpg,.jpeg,.gif,.webp"
                onChange={handleFileChange}
                className="hidden"
              />
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-white text-brand-red shadow-xs border border-gray-200">
                <i className="fa-solid fa-cloud-arrow-up text-lg" />
              </div>
              <p className="mt-3 text-sm font-semibold text-gray-800">
                Klik for at vælge filer, eller træk dem hertil
              </p>
              <p className="mt-1 text-xs text-gray-500">
                Understøtter billeder (JPG, PNG, WEBP, GIF) og dokumenter (PDF) op til 10 MB per fil.
              </p>
            </div>

            {/* Fejlbesked for fil-upload */}
            {fileError && (
              <p className="mt-2 text-xs font-semibold text-red-600 flex items-center gap-1.5">
                <i className="fa-solid fa-circle-exclamation" /> {fileError}
              </p>
            )}

            {/* Liste over valgte filer */}
            {files.length > 0 && (
              <div className="mt-4 space-y-2">
                <p className="text-xs font-bold uppercase tracking-wider text-gray-500">
                  Vedhæftede filer ({files.length}):
                </p>
                <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                  {files.map((file, idx) => (
                    <div
                      key={idx}
                      className="flex items-center justify-between gap-3 rounded-xl border border-gray-200 bg-white p-3 shadow-xs"
                    >
                      <div className="flex items-center gap-2.5 min-w-0">
                        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-gray-100 text-brand-red text-xs">
                          <i className={file.type.includes('pdf') ? 'fa-solid fa-file-pdf' : 'fa-solid fa-file-image'} />
                        </span>
                        <div className="min-w-0">
                          <p className="truncate text-xs font-semibold text-gray-800">{file.name}</p>
                          <p className="text-[11px] text-gray-400">{formatFileSize(file.size)}</p>
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          removeFile(idx);
                        }}
                        className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg text-gray-400 hover:bg-red-50 hover:text-red-600 transition-colors cursor-pointer"
                        aria-label={`Fjern ${file.name}`}
                      >
                        <i className="fa-solid fa-xmark text-xs" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Fejlbesked for server/formularfejl */}
          {status === 'error' && (
            <div className="rounded-xl bg-red-50 p-4 text-red-800 border border-red-200 flex items-start gap-3">
              <i className="fa-solid fa-circle-exclamation text-lg mt-0.5 text-red-600" />
              <p className="text-sm">{errorMessage}</p>
            </div>
          )}

          {/* Indsend knap */}
          <button
            type="submit"
            disabled={status === 'submitting'}
            className="flex w-full items-center justify-center gap-2 rounded-xl bg-brand-red py-4 px-6 text-sm font-semibold text-white shadow-sm transition-all hover:bg-brand-red-hover focus:ring-2 focus:ring-brand-red/20 focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
          >
            {status === 'submitting' ? (
              <>
                <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                Sender din forespørgsel...
              </>
            ) : (
              <>
                <i className="fa-solid fa-paper-plane" />
                Send forespørgsel
              </>
            )}
          </button>
        </form>
      )}
    </div>
  );
}
